/**
 * Thin fetch wrapper around the Worker's JSON API. All calls go through
 * relative URLs under `/api/*` so the Cloudflare Worker route
 * (`u2u.sentry.red/api/*`) catches them in production, and the Vite dev
 * proxy (`/api` → 127.0.0.1:8787) catches them locally.
 */

import type {
  CreateJobRequest,
  CreateJobResponse,
  FeeBreakdown,
  JobProgressResponse,
} from "@u2u/shared";

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${text.slice(0, 200)}`);
  }
  return (await res.json()) as T;
}

export async function createJob(
  body: CreateJobRequest
): Promise<CreateJobResponse> {
  return json(
    await fetch("/api/jobs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
  );
}

export async function getJob(token: string): Promise<JobProgressResponse> {
  return json(await fetch(`/api/jobs/${encodeURIComponent(token)}`));
}

export async function abortJob(token: string): Promise<void> {
  await json(
    await fetch(`/api/jobs/${encodeURIComponent(token)}/abort`, { method: "POST" })
  );
}

/**
 * `numSplits` is required because hops 5 and 6 are per-leg — the
 * authoritative math lives in packages/shared/src/fees.ts.
 */
export async function estimateFees(params: {
  amountUsdt: number;
  numSplits: number;
  gasGwei?: number;
  ethUsd?: number;
}): Promise<FeeBreakdown> {
  const q = new URLSearchParams({
    amount_usdt: params.amountUsdt.toString(),
    num_splits: params.numSplits.toString(),
    ...(params.gasGwei ? { gas_gwei: params.gasGwei.toString() } : {}),
    ...(params.ethUsd ? { eth_usd: params.ethUsd.toString() } : {}),
  });
  return json(await fetch(`/api/estimate/fees?${q.toString()}`));
}

export type TreasuryResponse =
  | { configured: false }
  | {
      configured: true;
      tron_address: string;
      trx_natural: number;
      usdt_natural: string;
    };

export async function getTreasury(): Promise<TreasuryResponse> {
  return json(await fetch("/api/treasury/balances"));
}
